import { useEffect, useState } from 'react';

/**
 * Karaoke-style dot: hovers just above whatever element it is aimed at and hops in place.
 * Point it somewhere new by handing it a different `targetRef` — it eases across on its
 * own.
 *
 * @param containerRef  positioned ancestor the dot is absolutely placed inside
 * @param targetRef     element to sit above; swapping this moves the dot
 * @param anchor        0–1 across the target's width (0.5 centres it)
 * @param gap           px of clearance between the dot and the top of the target
 * @param className     extra classes on the wrapper, e.g. responsive visibility
 */
export default function BouncingDot({
    containerRef,
    targetRef,
    anchor = 0.5,
    gap = 24,
    className = '',
}) {
    const [pos, setPos] = useState(null);

    useEffect(() => {
        const target = targetRef?.current;
        const container = containerRef?.current;
        if (!target || !container) return;

        // Measured off the target rather than given fixed coordinates, so the dot keeps
        // its aim when the copy rewraps at a different width.
        const measure = () => {
            const t = target.getBoundingClientRect();
            const c = container.getBoundingClientRect();
            setPos({ x: t.left - c.left + t.width * anchor, y: t.top - c.top });
        };
        measure();
        // A target that is still animating in reports where it is now, not where it
        // lands, so take the reading again once the reveal has settled.
        const settle = setTimeout(measure, 750);
        window.addEventListener('resize', measure);
        return () => {
            clearTimeout(settle);
            window.removeEventListener('resize', measure);
        };
    }, [targetRef, containerRef, anchor]);

    if (!pos) return null;

    return (
        <div
            aria-hidden="true"
            className={`absolute z-20 pointer-events-none transition-all duration-500 ease-out ${className}`}
            style={{ left: pos.x, top: pos.y - gap }}
        >
            {/* The wrapper carries the position and eases between targets; the dot
                carries the bounce, since a keyframe transform would overwrite any
                centring transform sharing the same element. */}
            <span className="block h-3 w-3 rounded-full bg-white shadow-[0_0_10px_rgba(255,255,255,0.7)] animate-bounce-dot" />
        </div>
    );
}
